import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { API_BASE_URL } from '../../config/api';
import { getStoredUsername, saveStoredUsername } from '../../utils/usernameStorage';

const Star = ({ type = 'full', className = 'w-5 h-5', interactive = false, onClick }) => {
  const handleClick = () => {
    if (interactive && onClick) onClick();
  };

  if (type === 'half') {
    return (
      <svg 
        viewBox="0 0 20 20" 
        className={`${className} ${interactive ? 'cursor-pointer hover:scale-110 transition-transform' : ''}`} 
        aria-hidden="true"
        onClick={handleClick}
      >
        <defs>
          <linearGradient id="half-fill" x1="0" x2="1">
            <stop offset="50%" stopColor="#ffb400" />
            <stop offset="50%" stopColor="transparent" />
          </linearGradient>
        </defs>
        <path
          d="M10 1.5l2.6 5.3 5.9.9-4.2 4.1 1 5.8L10 14.7 4.7 17.6l1-5.8L1.5 7.7l5.9-.9L10 1.5z"
          fill="url(#half-fill)"
          stroke="#ffb400"
          strokeWidth="1"
        />
      </svg>
    );
  }
  if (type === 'empty') {
    return (
      <svg 
        viewBox="0 0 20 20" 
        className={`${className} ${interactive ? 'cursor-pointer hover:scale-110 transition-transform' : ''}`} 
        aria-hidden="true"
        onClick={handleClick}
      >
        <path
          d="M10 1.5l2.6 5.3 5.9.9-4.2 4.1 1 5.8L10 14.7 4.7 17.6l1-5.8L1.5 7.7l5.9-.9L10 1.5z"
          fill="transparent"
          stroke="#475569"
          strokeWidth="1"
        />
      </svg>
    );
  }
  return (
    <svg 
      viewBox="0 0 20 20" 
      className={`${className} ${interactive ? 'cursor-pointer hover:scale-110 transition-transform' : ''}`} 
      aria-hidden="true"
      onClick={handleClick}
    >
      <path
        d="M10 1.5l2.6 5.3 5.9.9-4.2 4.1 1 5.8L10 14.7 4.7 17.6l1-5.8L1.5 7.7l5.9-.9L10 1.5z"
        fill="#ffb400"
        stroke="#ffb400"
        strokeWidth="1"
      />
    </svg>
  );
};

const Stars = ({ rating, interactive = false, onRatingChange, className = '' }) => {
  const handleStarClick = (newRating) => {
    if (interactive && onRatingChange) {
      onRatingChange(newRating);
    }
  };

  const items = [];
  for (let i = 1; i <= 5; i++) {
    if (i <= rating) {
      items.push(
        <Star 
          key={i} 
          className={`w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 ${className}`}
          interactive={interactive} 
          onClick={() => handleStarClick(i)}
        />
      );
    } else {
      items.push(
        <Star 
          key={i} 
          className={`w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 ${className}`}
          type="empty" 
          interactive={interactive} 
          onClick={() => handleStarClick(i)}
        />
      );
    }
  }
  return <div className="flex items-center gap-0.5 sm:gap-1">{items}</div>;
};

const PlayerDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  
  const [player, setPlayer] = useState(location.state?.player || null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userRating, setUserRating] = useState(5);
  const [sortBy, setSortBy] = useState('Latest');
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewForm, setReviewForm] = useState({ username: '', reviewText: '' });
  const [submitting, setSubmitting] = useState(false);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (showReviewModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showReviewModal]);

  // On mount, load username from localStorage once
  useEffect(() => {
    const savedUsername = getStoredUsername();
    if (savedUsername) {
      setReviewForm((prev) => ({ ...prev, username: savedUsername }));
    }
  }, []);

  useEffect(() => {
    if (!player && id) {
      fetchPlayer();
    } else {
      fetchReviews();
    }
  }, [id, player]);

  const fetchPlayer = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/review-players/${id}`);
      if (response.ok) {
        const data = await response.json();
        setPlayer(data);
        fetchReviews();
      }
    } catch (error) {
      console.error('Error fetching player:', error);
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/review-players/${id || player?._id}/reviews`);
      if (response.ok) {
        const data = await response.json();
        setReviews(data);
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    
    if (!reviewForm.username.trim() || !reviewForm.reviewText.trim()) {
      alert('Please fill in all fields');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/review-players/${player._id}/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: reviewForm.username.trim(),
          rating: userRating,
          reviewText: reviewForm.reviewText
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        const savedUsername = saveStoredUsername(reviewForm.username);
        alert('Review submitted successfully!');
        setShowReviewModal(false);
        setReviewForm({ username: savedUsername || reviewForm.username.trim(), reviewText: '' });
        setUserRating(5);
        fetchReviews();
        fetchPlayer();
      } else {
        alert(data.message || 'Failed to submit review');
      }
    } catch (error) {
      console.error('Error submitting review:', error);
      alert('Error submitting review');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLikeReview = async (reviewId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/reviews/${reviewId}/like`, {
        method: 'POST'
      });
      
      if (response.ok) {
        fetchReviews();
      }
    } catch (error) {
      console.error('Error liking review:', error);
    }
  };

  const formatNum = (n) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : `${n}`);

  const sortedReviews = [...reviews].sort((a, b) => {
    if (sortBy === 'Latest') {
      return new Date(b.createdAt) - new Date(a.createdAt);
    } else {
      return b.likes - a.likes;
    }
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-b-4 border-yellow-400 mx-auto mb-4"></div>
          <p className="text-white text-base sm:text-xl">Loading...</p>
        </div>
      </div>
    );
  }

  if (!player) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center px-4">
          <h1 className="text-xl sm:text-2xl font-bold mb-4">Player not found</h1>
          <button 
            onClick={() => navigate('/player-review')}
            className="px-4 sm:px-6 py-2 sm:py-3 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm sm:text-base transition-colors"
          >
            Back to Players
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen text-white overflow-x-hidden"
      style={{
        backgroundColor: '#0b0c10',
        backgroundImage:
          'linear-gradient(rgba(255,255,255,0.09) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.09) 1px, transparent 1px), radial-gradient(rgba(255,255,255,0.24) 1px, transparent 1.4px)',
        backgroundSize: '64px 64px, 64px 64px, 64px 64px',
        backgroundPosition: 'center center, center center, 32px 32px',
      }}
    >
      {/* Header with Earth Reviews branding - Fully Responsive */}
      <div className="absolute top-2 left-2 sm:top-3 sm:left-3 md:top-4 md:left-4 z-10 bg-black/40 backdrop-blur-sm px-2 py-1 sm:px-3 sm:py-1.5 rounded">
        <div className="flex items-center space-x-1 sm:space-x-2 text-white text-[10px] xs:text-xs sm:text-sm">
          <span className="text-yellow-400">★</span>
          <span className="font-bold whitespace-nowrap">EARTH REVIEWS</span>
          <span className="text-yellow-400">★</span>
        </div>
      </div>

      {/* Main Content Container - Ultra Responsive */}
      <div className="max-w-6xl mx-auto px-3 sm:px-4 md:px-6 pt-12 sm:pt-14 md:pt-16">
        {/* Player Header Section - Fully Responsive Layout */}
        <div className="flex flex-col items-center sm:items-start sm:flex-row gap-4 sm:gap-5 md:gap-6 mb-6 sm:mb-8">
          {/* Player Image - Responsive Sizing with better mobile centering */}
          <div className="relative w-24 h-24 xs:w-28 xs:h-28 sm:w-32 sm:h-32 md:w-36 md:h-36 lg:w-40 lg:h-40 rounded-full overflow-hidden border-2 sm:border-3 md:border-4 border-white bg-blue-500 flex-shrink-0 shadow-2xl">
            {player.image ? (
              <img 
                src={player.image}
                alt={player.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-500 to-purple-600">
                <span className="text-3xl sm:text-4xl md:text-5xl font-bold text-white">
                  {player.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                </span>
              </div>
            )}
          </div>
          
          {/* Player Info - Responsive Typography and Layout */}
          <div className="flex-1 text-center sm:text-left w-full">
            <h1 className="text-xl xs:text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2 sm:mb-3 px-2 sm:px-0 leading-tight">
              {player.name}
            </h1>
            <p className="text-gray-300 text-xs xs:text-sm sm:text-base md:text-lg mb-3 sm:mb-4 px-2 sm:px-0">
              {player.description || `${player.team}${player.position ? ' • ' + player.position : ''}`}
            </p>
            
            {/* Rating Display - Fully Responsive */}
            <div className="flex flex-col xs:flex-row items-center justify-center sm:justify-start gap-2 sm:gap-3 mb-2">
              <Stars rating={Math.floor(player.rating || 0)} />
              <div className="flex items-center gap-2 text-xs xs:text-sm sm:text-base md:text-lg">
                <span className="text-white font-bold">
                  {(player.rating || 0).toFixed(1)} / 5
                </span>
                <span className="text-gray-400 text-[10px] xs:text-xs sm:text-sm">
                  ({formatNum(player.ratingsCount || 0)} ratings)
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Your Rating Section - Ultra Responsive */}
        <div className="mb-6 sm:mb-8">
          <div 
            className="border border-white/30 p-4 sm:p-5 md:p-6 relative overflow-hidden"
            style={{ 
              backgroundColor: '#000',
              clipPath: 'polygon(0 0, calc(100% - 20px) 0, 100% 100%, 0 100%)'
            }}
          >
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="w-full sm:w-auto">
                <h3 className="text-white text-sm sm:text-base md:text-lg font-semibold mb-3">Your rating</h3>
                <div className="flex justify-center sm:justify-start">
                  <Stars 
                    rating={userRating} 
                    interactive={true} 
                    onRatingChange={setUserRating}
                  />
                </div>
              </div>
              <button 
                onClick={() => setShowReviewModal(true)}
                className="bg-white text-black px-4 sm:px-5 md:px-6 py-2 sm:py-2.5 font-semibold hover:bg-gray-200 transition-colors text-sm sm:text-base w-full sm:w-auto shadow-lg hover:shadow-xl transform hover:scale-105 duration-200"
              >
                Write a review
              </button>
            </div>
          </div>
        </div>

        {/* Reviews Section - Fully Responsive */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 mb-4 sm:mb-6">
            <h2 className="text-white text-lg sm:text-xl md:text-2xl font-bold">{reviews.length} Reviews</h2>
            
            {/* Sort Buttons - Responsive Design */}
            <div className="flex gap-2 w-full sm:w-auto">
              <button 
                className={`flex-1 sm:flex-none px-3 sm:px-4 md:px-5 py-2 border transition-all duration-200 text-xs sm:text-sm md:text-base font-medium ${
                  sortBy === 'Latest' 
                    ? 'border-white text-white bg-white/10' 
                    : 'border-gray-600 text-gray-400 hover:border-white hover:text-white hover:bg-white/5'
                }`}
                onClick={() => setSortBy('Latest')}
              >
                Latest
              </button>
              <button 
                className={`flex-1 sm:flex-none px-3 sm:px-4 md:px-5 py-2 border transition-all duration-200 text-xs sm:text-sm md:text-base font-medium ${
                  sortBy === 'Top' 
                    ? 'border-white text-white bg-white/10' 
                    : 'border-gray-600 text-gray-400 hover:border-white hover:text-white hover:bg-white/5'
                }`}
                onClick={() => setSortBy('Top')}
              >
                Top
              </button>
            </div>
          </div>

          {/* Review Items - Ultra Responsive Cards */}
          <div className="space-y-3 sm:space-y-4">
            {sortedReviews.length === 0 ? (
              <div className="text-center py-8 sm:py-12 text-gray-400 border border-white/20 rounded-lg bg-black/40">
                <div className="text-3xl sm:text-4xl mb-3">📝</div>
                <p className="text-sm sm:text-base">No reviews yet. Be the first to review!</p>
              </div>
            ) : (
              sortedReviews.map((review) => (
                <div 
                  key={review._id}
                  className="border border-white/30 bg-black/60 backdrop-blur-sm p-3 sm:p-4 md:p-6 rounded-lg hover:border-white/50 transition-all duration-200 hover:shadow-xl"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h4 className="text-gray-300 font-semibold mb-2 text-sm sm:text-base">{review.username}</h4>
                      <p className="text-white text-xs sm:text-sm md:text-base leading-relaxed mb-3 pr-2 break-words">
                        {review.reviewText}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex flex-col xs:flex-row items-start xs:items-center justify-between gap-3">
                    <Stars rating={review.rating} />
                    <button 
                      onClick={() => handleLikeReview(review._id)}
                      className="flex items-center gap-2 text-gray-400 hover:text-yellow-400 transition-colors group"
                    >
                      <svg className="w-4 h-4 sm:w-5 sm:h-5 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                      <span className="text-xs sm:text-sm md:text-base font-medium">{review.likes}</span>
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Back Button - Responsive */}
        <div className="pb-6 sm:pb-8">
          <button 
            onClick={() => navigate('/player-review')}
            className="inline-flex items-center border border-white/30 text-white hover:bg-white/10 hover:border-white/50 font-medium text-sm px-4 py-2 rounded transition-all backdrop-blur-sm"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span>Back to Players</span>
          </button>
        </div>
      </div>

      {/* Review Modal - ULTRA RESPONSIVE */}
      {showReviewModal && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[9999] p-3 sm:p-4 overflow-y-auto"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowReviewModal(false);
            }
          }}
        >
          <div 
            className="bg-[#0b0c10] border border-white/30 rounded-lg w-full max-w-md shadow-2xl my-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 sm:p-6">
              <div className="flex justify-between items-center mb-4 sm:mb-6 pb-3 border-b border-white/20">
                <h3 className="text-white text-lg sm:text-xl font-bold">Write Your Review</h3>
                <button
                  type="button"
                  onClick={() => setShowReviewModal(false)}
                  className="text-gray-400 hover:text-white p-2 hover:bg-white/10 rounded-full transition-colors"
                >
                  <span className="text-2xl leading-none">×</span>
                </button>
              </div>
              
              <form onSubmit={handleSubmitReview} className="space-y-4">
                <div>
                  <label className="block text-white text-xs sm:text-sm font-medium mb-2">Your Name</label>
                  <input
                    type="text"
                    required
                    value={reviewForm.username}
                    onChange={(e) => setReviewForm({ ...reviewForm, username: e.target.value })}
                    className="w-full px-3 py-2 sm:py-3 bg-black border border-white/30 text-white rounded-md focus:outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/20 text-sm sm:text-base transition-all"
                    placeholder="Enter your name"
                  />
                </div>

                <div>
                  <label className="block text-white text-xs sm:text-sm font-medium mb-2">Your Rating</label>
                  <div className="flex justify-center sm:justify-start">
                    <Stars 
                      rating={userRating} 
                      interactive={true} 
                      onRatingChange={setUserRating}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-white text-xs sm:text-sm font-medium mb-2">Your Review</label>
                  <textarea
                    required
                    value={reviewForm.reviewText}
                    onChange={(e) => setReviewForm({ ...reviewForm, reviewText: e.target.value })}
                    className="w-full px-3 py-2 sm:py-3 bg-black border border-white/30 text-white rounded-md focus:outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/20 h-28 sm:h-32 resize-none text-sm sm:text-base transition-all"
                    placeholder="Share your thoughts..."
                  />
                </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 bg-white text-black px-4 py-2 sm:py-3 font-semibold rounded-md hover:bg-gray-200 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed text-sm sm:text-base shadow-lg hover:shadow-xl transform hover:scale-105 duration-200"
                  >
                    {submitting ? 'Submitting...' : 'Submit Review'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowReviewModal(false)}
                    className="sm:flex-none px-4 py-2 sm:py-3 border border-white/30 text-white rounded-md hover:bg-white/10 transition-colors text-sm sm:text-base"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Custom responsive utilities */}
      <style jsx>{`
        @media (min-width: 375px) {
          .xs\\:text-xs {
            font-size: 0.75rem;
          }
          .xs\\:text-sm {
            font-size: 0.875rem;
          }
          .xs\\:text-2xl {
            font-size: 1.5rem;
          }
          .xs\\:w-28 {
            width: 7rem;
          }
          .xs\\:h-28 {
            height: 7rem;
          }
          .xs\\:flex-row {
            flex-direction: row;
          }
          .xs\\:items-center {
            align-items: center;
          }
        }

        /* Smooth scrolling */
        html {
          scroll-behavior: smooth;
        }

        /* Prevent horizontal scroll */
        body {
          overflow-x: hidden;
        }

        /* Better word breaking for long text */
        .break-words {
          word-wrap: break-word;
          overflow-wrap: break-word;
        }

        /* Touch-friendly tap highlight */
        * {
          -webkit-tap-highlight-color: rgba(255, 180, 0, 0.2);
        }

        /* Improved scrollbar for webkit browsers */
        ::-webkit-scrollbar {
          width: 8px;
        }

        ::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.5);
        }

        ::-webkit-scrollbar-thumb {
          background: rgba(255, 180, 0, 0.5);
          border-radius: 4px;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 180, 0, 0.7);
        }
      `}</style>
    </div>
  );
};

export default PlayerDetail;
